import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'
import { ConfirmModal } from '../components/ConfirmModal'

interface Migration {
  id: string
  name: string
  description: string
  sql: string
  isIdempotent: boolean
}

interface MigrationHistoryEntry {
  migrationId: string
  migrationName: string
  ranAt: string
  ranBy: string
  success: boolean
  errorMessage?: string
}

type ModalState =
  | { type: 'none' }
  | { type: 'confirm'; migration: Migration }
  | { type: 'viewSql'; migration: Migration }

export function MigrationsPage() {
  const { apiClient, isSuperUser } = useAuth()
  const [migrations, setMigrations] = useState<Migration[]>([])
  const [history, setHistory] = useState<MigrationHistoryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [runningMigration, setRunningMigration] = useState<string | null>(null)
  const [modal, setModal] = useState<ModalState>({ type: 'none' })

  const loadData = useCallback(async () => {
    if (!apiClient) return

    try {
      setLoading(true)
      setError(null)
      const [migrationsRes, historyRes] = await Promise.all([
        apiClient.getMigrations(),
        apiClient.getMigrationHistory(),
      ])
      setMigrations(migrationsRes.migrations)
      setHistory(historyRes.history)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Failed to load migrations')
      }
    } finally {
      setLoading(false)
    }
  }, [apiClient])

  useEffect(() => {
    loadData()
  }, [loadData])

  const runMigration = async (migration: Migration) => {
    if (!apiClient) return

    try {
      setRunningMigration(migration.id)
      setError(null)
      await apiClient.runMigration(migration.id)
      setModal({ type: 'none' })
      await loadData()
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Migration failed: ${err.message}`)
      } else {
        setError('Migration failed')
      }
    } finally {
      setRunningMigration(null)
    }
  }

  const getMigrationStatus = (migrationId: string): 'pending' | 'completed' | 'failed' => {
    const historyEntry = history.find(h => h.migrationId === migrationId)
    if (!historyEntry) return 'pending'
    return historyEntry.success ? 'completed' : 'failed'
  }

  const getLastRun = (migrationId: string): MigrationHistoryEntry | undefined => {
    return history
      .filter(h => h.migrationId === migrationId)
      .sort((a, b) => new Date(b.ranAt).getTime() - new Date(a.ranAt).getTime())[0]
  }

  if (!isSuperUser) {
    return (
      <div style={{ padding: 'var(--spacing-lg)', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You must be a super user to access migrations.</p>
      </div>
    )
  }

  return (
    <div style={{ padding: 'var(--spacing-lg)', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <h2 style={{ margin: 0 }}>Database Migrations</h2>
        <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-xs)' }}>
          Manage schema changes and data migrations. All migrations are idempotent and safe to re-run.
        </p>
      </div>

      {error && (
        <div style={{
          padding: 'var(--spacing-md)',
          backgroundColor: 'var(--color-danger-bg, rgba(220, 38, 38, 0.1))',
          border: '1px solid var(--color-danger)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--spacing-lg)',
          color: 'var(--color-danger)',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 'var(--spacing-xl)' }}>
          Loading migrations...
        </div>
      ) : (
        <>
          {/* Available Migrations */}
          <div style={{ marginBottom: 'var(--spacing-xl)' }}>
            <h3 style={{ marginBottom: 'var(--spacing-md)' }}>Available Migrations</h3>
            {migrations.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)' }}>No migrations available.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                {migrations.map((migration) => {
                  const status = getMigrationStatus(migration.id)
                  const lastRun = getLastRun(migration.id)
                  const isRunning = runningMigration === migration.id

                  return (
                    <div
                      key={migration.id}
                      style={{
                        padding: 'var(--spacing-md)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: 'var(--color-bg-secondary)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                            <span style={{ fontWeight: 600 }}>{migration.name}</span>
                            <span
                              className="badge"
                              style={{
                                backgroundColor:
                                  status === 'completed' ? 'var(--color-success)' :
                                  status === 'failed' ? 'var(--color-danger)' :
                                  'var(--color-text-muted)',
                                color: status === 'pending' ? 'var(--color-bg-primary)' : '#fff',
                              }}
                            >
                              {status}
                            </span>
                            {migration.isIdempotent && (
                              <span className="badge" style={{ backgroundColor: 'var(--color-info, #3b82f6)', color: '#fff' }}>
                                idempotent
                              </span>
                            )}
                          </div>
                          <p style={{ margin: 'var(--spacing-xs) 0 0 0', color: 'var(--color-text-secondary)' }}>
                            {migration.description}
                          </p>
                          {lastRun && (
                            <p style={{ margin: 'var(--spacing-xs) 0 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                              Last run: {new Date(lastRun.ranAt).toLocaleString()} by {lastRun.ranBy}
                              {!lastRun.success && lastRun.errorMessage && (
                                <span style={{ color: 'var(--color-danger)' }}> - {lastRun.errorMessage}</span>
                              )}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                          <button
                            className="btn btn-secondary"
                            onClick={() => setModal({ type: 'viewSql', migration })}
                            style={{ fontSize: 'var(--font-size-sm)' }}
                          >
                            View SQL
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={() => setModal({ type: 'confirm', migration })}
                            disabled={isRunning}
                            style={{ fontSize: 'var(--font-size-sm)' }}
                          >
                            {isRunning ? 'Running...' : status === 'completed' ? 'Re-run' : 'Run'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Migration History */}
          <div>
            <h3 style={{ marginBottom: 'var(--spacing-md)' }}>Migration History</h3>
            {history.length === 0 ? (
              <p style={{ color: 'var(--color-text-muted)' }}>No migrations have been run yet.</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Migration</th>
                      <th>Status</th>
                      <th>Run At</th>
                      <th>Run By</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history
                      .sort((a, b) => new Date(b.ranAt).getTime() - new Date(a.ranAt).getTime())
                      .map((entry, idx) => (
                        <tr key={`${entry.migrationId}-${idx}`}>
                          <td>{entry.migrationName}</td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                backgroundColor: entry.success ? 'var(--color-success)' : 'var(--color-danger)',
                                color: '#fff',
                              }}
                            >
                              {entry.success ? 'success' : 'failed'}
                            </span>
                          </td>
                          <td>{new Date(entry.ranAt).toLocaleString()}</td>
                          <td>{entry.ranBy}</td>
                          <td style={{ color: 'var(--color-danger)' }}>{entry.errorMessage || '-'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* View SQL Modal */}
      {modal.type === 'viewSql' && (
        <div className="modal-overlay" onClick={() => setModal({ type: 'none' })}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Migration SQL: {modal.migration.name}</h3>
            </div>
            <div style={{ padding: 'var(--spacing-md)' }}>
              <pre style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                padding: 'var(--spacing-md)',
                borderRadius: 'var(--radius-md)',
                overflow: 'auto',
                maxHeight: '400px',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-mono)',
              }}>
                {modal.migration.sql}
              </pre>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal({ type: 'none' })}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Run Modal */}
      {modal.type === 'confirm' && (
        <ConfirmModal
          title="Run Migration"
          confirmLabel={runningMigration ? 'Running...' : 'Run Migration'}
          onConfirm={() => runMigration(modal.migration)}
          onCancel={() => setModal({ type: 'none' })}
          isLoading={!!runningMigration}
        >
          <div style={{ padding: 'var(--spacing-md)' }}>
            <p style={{ margin: '0 0 var(--spacing-md) 0' }}>
              Are you sure you want to run the migration "{modal.migration.name}"? This will execute the following SQL against the database.
            </p>
            <pre style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              padding: 'var(--spacing-md)',
              borderRadius: 'var(--radius-md)',
              overflow: 'auto',
              maxHeight: '200px',
              fontSize: 'var(--font-size-xs)',
              fontFamily: 'var(--font-family-mono)',
              margin: 0,
            }}>
              {modal.migration.sql}
            </pre>
          </div>
        </ConfirmModal>
      )}
    </div>
  )
}
